/**
 * 정산 분류·예외 수업료 저장 실패를 사람이 읽을 문구로 바꾼다.
 *
 * 관용구는 `features/ticketTransfer/errors.ts` 와 같다 — 분기는 errorCode 로 하고,
 * 모르는 코드는 서버 message 또는 기본 문구로 둔다.
 */
export type SettlementClassificationFailure = {
    status?: number;
    /** ky apiClient 가 구조화 오류 본문을 보존하는 필드. */
    body?: unknown;
    /** axios 호출부가 넘기는 response.data 형태. */
    data?: unknown;
    /** axios 오류 객체를 그대로 넘기는 경우. */
    response?: { status?: number; data?: unknown };
    message?: string;
    serverMessage?: string;
};

const payloadOf = (failure: SettlementClassificationFailure | null | undefined): unknown =>
    failure?.body ?? failure?.data ?? failure?.response?.data;

const statusOf = (failure: SettlementClassificationFailure | null | undefined): number | undefined =>
    failure?.status ?? failure?.response?.status;

export const getSettlementClassificationErrorCode = (
    failure: SettlementClassificationFailure | null | undefined,
): string => {
    const payload = payloadOf(failure);
    if (!payload || typeof payload !== 'object') return '';
    const errorCode = (payload as { errorCode?: unknown }).errorCode;
    return typeof errorCode === 'string' ? errorCode.trim() : '';
};

const readRuleIndex = (failure: SettlementClassificationFailure | null | undefined): number | null => {
    const payload = payloadOf(failure);
    if (!payload || typeof payload !== 'object') return null;
    const details = (payload as { details?: unknown }).details;
    if (!details || typeof details !== 'object') return null;
    const index = (details as { index?: unknown }).index;
    return typeof index === 'number' && Number.isSafeInteger(index) && index >= 0 ? index : null;
};

const KNOWN_MESSAGES: Record<string, string> = {
    SETTLEMENT_CATEGORY_NAME_DUPLICATED: '같은 이름의 분류가 이미 있어요. 보관한 분류와도 이름이 겹치면 안 돼요.',
    SETTLEMENT_CATEGORY_LIMIT_EXCEEDED: '분류는 50개까지 만들 수 있어요.',
    SETTLEMENT_CATEGORY_CONFLICT: '다른 곳에서 동시에 고치고 있어요. 잠시 후 다시 시도해 주세요.',
    SETTLEMENT_CATEGORY_NOT_FOUND: '없는 분류예요. 새로고침한 뒤 다시 골라 주세요.',
    SETTLEMENT_CATEGORY_ARCHIVED: '보관한 분류는 새로 고를 수 없어요. 다른 분류를 골라 주세요.',
    SETTLEMENT_CATEGORY_ADMIN_ONLY: '정산 분류는 센터 관리자만 바꿀 수 있어요.',
    SETTLEMENT_CATEGORY_DANGLING: '상품의 정산 분류를 찾을 수 없어요. 상품의 정산 분류를 다시 골라 주세요.',
    COACH_NOT_IN_CENTER: '이 센터에 연결된 강사가 아니에요. 강사 목록을 새로고침해 주세요.',
    EXCEPTION_RATES_CONFLICT: '요율 저장과 겹쳤어요. 잠시 후 다시 저장해 주세요.',
    // 409 — 형식 표식이 없는 저장(이전 화면)이 요율·재등록·노쇼 값을 지울 수 있어 서버가 막았다.
    EXCEPTION_RATES_CLIENT_OUTDATED: '화면을 새로고침한 뒤 다시 저장해 주세요.',
    MEMBERSHIP_NOT_FOUND: '이 센터 수강권이 아니어서 고칠 수 없어요.',
    SETTLEMENT_SNAPSHOT_EMPTY_PATCH: '바꾼 값이 없어요.',
    // 상품 분류 일괄 적용(BACKEND_HANDOFF_TEMPLATE_BULK). IN_PROGRESS 는 정산 기준 정정에서도 온다(§6-1).
    SETTLEMENT_BULK_IN_PROGRESS: '같은 상품에 일괄 적용이 진행 중이에요. 잠시 후 다시 시도해 주세요.',
    SETTLEMENT_BULK_SCAN_TIMEOUT: '수강권을 확인하는 데 시간이 오래 걸려요. 잠시 후 다시 시도해 주세요.',
    SETTLEMENT_BULK_TEMPLATE_NOT_FOUND: '상품을 찾을 수 없어요. 새로고침한 뒤 다시 확인해 주세요.',
    SETTLEMENT_BULK_JOB_NOT_FOUND: '되돌릴 작업을 찾을 수 없어요. 새로고침해 주세요.',
    SETTLEMENT_BULK_NOT_REVERTIBLE: '적용이 아직 끝나지 않아 되돌릴 수 없어요. 잠시 후 다시 시도해 주세요.',
};

export const DEFAULT_SETTLEMENT_CLASSIFICATION_ERROR = '저장하지 못했어요. 잠시 후 다시 시도해 주세요.';

/**
 * 항상 문구를 돌려준다(화면 안 오류 줄에 그대로 쓴다).
 * - 아는 errorCode → 정해 둔 문구
 * - errorCode 없는 404 → 구서버(기능 미배포)
 * - 그 밖 → 서버 message, 없으면 기본 문구
 */
export const getSettlementClassificationErrorMessage = (
    failure: SettlementClassificationFailure | null | undefined,
): string => {
    // 401 — 공용 클라이언트가 갱신·재전송하지 않았다(noAuthRetry).
    if ((failure as { authRetrySkipped?: boolean } | null | undefined)?.authRetrySkipped || statusOf(failure) === 401) {
        return '로그인이 바뀌었거나 만료됐어요. 다시 로그인해 주세요.';
    }
    const errorCode = getSettlementClassificationErrorCode(failure);
    if (errorCode === 'EXCEPTION_RATES_INVALID') {
        const index = readRuleIndex(failure);
        return index === null ? '예외 규칙 값을 확인해 주세요.' : `${index + 1}번째 예외 규칙 값을 확인해 주세요.`;
    }
    if (errorCode && KNOWN_MESSAGES[errorCode]) return KNOWN_MESSAGES[errorCode];
    if (!errorCode && statusOf(failure) === 404) return '아직 이 기능을 쓸 수 없어요. 새로고침 후에도 같으면 라포채널로 문의해 주세요.';
    const serverMessage = failure?.serverMessage;
    if (errorCode && typeof serverMessage === 'string' && serverMessage.trim()) return serverMessage.trim();
    return DEFAULT_SETTLEMENT_CLASSIFICATION_ERROR;
};
